import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { authDebug } from '@/lib/authDebugInstrumentation';
import type { DeviceObservationSource, DeviceTrustState } from '@/services/deviceTrust';

export type PanelSessionErrorCode =
  | 'internal_access_invalid'
  | 'mfa_aal2_required'
  | 'panel_session_limit_reached'
  | 'panel_session_required'
  | 'panel_session_expired'
  | 'panel_session_not_found'
  | 'cannot_close_current_panel_session';

export interface PanelSession {
  id: number;
  actorId: string;
  openedAt: string;
  expiresAt: string;
  closedAt: string | null;
}

export interface PanelSessionState {
  hasValidPanelSession: boolean;
  openedAt: string | null;
  expiresAt: string | null;
  failureCode: Extract<PanelSessionErrorCode, 'panel_session_required' | 'panel_session_expired'> | null;
}

export interface PanelSessionRecoveryResult {
  closedSessionCount: number;
  readyForRetry: boolean;
}

export interface ManagedPanelSession {
  panelSessionPublicId: string;
  openedAt: string;
  expiresAt: string;
  closedAt: string | null;
  isCurrentSession: boolean;
  actorDeviceId: string | null;
  devicePublicId: string | null;
  deviceDisplayName: string | null;
  trustState: DeviceTrustState | null;
  lastObservedAt: string | null;
  lastObservationSource: DeviceObservationSource | null;
}

export interface ClosePanelSessionByPublicIdResult {
  closedSessionPublicId: string;
  wasClosed: boolean;
  closedAt: string | null;
}

export interface CloseOtherPanelSessionsResult {
  currentSessionPublicId: string;
  closedSessionCount: number;
}

export interface CloseAllPanelSessionsResult {
  currentSessionPublicId: string;
  closedSessionCount: number;
}

type RpcPanelSessionRow = {
  id: number;
  actor_id: string;
  opened_at: string;
  expires_at: string;
  closed_at: string | null;
};

type RpcPanelSessionStateRow = {
  has_valid_panel_session: boolean;
  opened_at: string | null;
  expires_at: string | null;
  failure_code: 'panel_session_required' | 'panel_session_expired' | null;
};

type RpcPanelSessionRecoveryRow = {
  closed_session_count: number;
  ready_for_retry: boolean;
};

type RpcManagedPanelSessionRow = {
  panel_session_public_id: string;
  opened_at: string;
  expires_at: string;
  closed_at: string | null;
  is_current_session: boolean;
  actor_device_id: string | null;
  device_public_id: string | null;
  device_display_name: string | null;
  trust_state: DeviceTrustState | null;
  last_observed_at: string | null;
  last_observation_source: DeviceObservationSource | null;
};

type RpcClosePanelSessionByPublicIdRow = {
  closed_session_public_id: string;
  was_closed: boolean;
  closed_at: string | null;
};

type RpcCloseOtherPanelSessionsRow = {
  current_session_public_id: string;
  closed_session_count: number;
};

type RpcCloseAllPanelSessionsRow = {
  current_session_public_id: string;
  closed_session_count: number;
};

const PANEL_SESSION_ERROR_CODES: PanelSessionErrorCode[] = [
  'internal_access_invalid',
  'mfa_aal2_required',
  'panel_session_limit_reached',
  'panel_session_required',
  'panel_session_expired',
  'panel_session_not_found',
  'cannot_close_current_panel_session',
];

function isPanelSessionErrorCode(value: unknown): value is PanelSessionErrorCode {
  return typeof value === 'string' && PANEL_SESSION_ERROR_CODES.includes(value as PanelSessionErrorCode);
}

function extractErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const message = 'message' in error ? error.message : null;
  return typeof message === 'string' && message.trim().length > 0 ? message.trim() : null;
}

export class PanelSessionError extends Error {
  readonly code: PanelSessionErrorCode;
  readonly details?: string | null;
  readonly hint?: string | null;
  readonly cause?: unknown;

  constructor(params: {
    code: PanelSessionErrorCode;
    message?: string;
    details?: string | null;
    hint?: string | null;
    cause?: unknown;
  }) {
    super(params.message ?? params.code);
    this.name = 'PanelSessionError';
    this.code = params.code;
    this.details = params.details;
    this.hint = params.hint;
    this.cause = params.cause;
  }
}

function normalizePanelSessionError(error: PostgrestError | unknown): PanelSessionError | Error {
  const message = extractErrorMessage(error);

  if (isPanelSessionErrorCode(message)) {
    const details = error && typeof error === 'object' && 'details' in error ? (error as { details?: string | null }).details : undefined;
    const hint = error && typeof error === 'object' && 'hint' in error ? (error as { hint?: string | null }).hint : undefined;

    return new PanelSessionError({
      code: message,
      details,
      hint,
      cause: error,
    });
  }

  return error instanceof Error ? error : new Error('No se pudo procesar la panel session.');
}

function mapPanelSessionRow(row: RpcPanelSessionRow): PanelSession {
  return {
    id: row.id,
    actorId: row.actor_id,
    openedAt: row.opened_at,
    expiresAt: row.expires_at,
    closedAt: row.closed_at,
  };
}

function mapPanelSessionStateRow(row: RpcPanelSessionStateRow): PanelSessionState {
  return {
    hasValidPanelSession: row.has_valid_panel_session,
    openedAt: row.opened_at,
    expiresAt: row.expires_at,
    failureCode: row.failure_code,
  };
}

function mapPanelSessionRecoveryRow(row: RpcPanelSessionRecoveryRow): PanelSessionRecoveryResult {
  return {
    closedSessionCount: row.closed_session_count,
    readyForRetry: row.ready_for_retry,
  };
}

function mapManagedPanelSessionRow(row: RpcManagedPanelSessionRow): ManagedPanelSession {
  return {
    panelSessionPublicId: row.panel_session_public_id,
    openedAt: row.opened_at,
    expiresAt: row.expires_at,
    closedAt: row.closed_at,
    isCurrentSession: row.is_current_session,
    actorDeviceId: row.actor_device_id,
    devicePublicId: row.device_public_id,
    deviceDisplayName: row.device_display_name,
    trustState: row.trust_state,
    lastObservedAt: row.last_observed_at,
    lastObservationSource: row.last_observation_source,
  };
}

function mapClosePanelSessionByPublicIdRow(
  row: RpcClosePanelSessionByPublicIdRow,
): ClosePanelSessionByPublicIdResult {
  return {
    closedSessionPublicId: row.closed_session_public_id,
    wasClosed: row.was_closed,
    closedAt: row.closed_at,
  };
}

function mapCloseOtherPanelSessionsRow(
  row: RpcCloseOtherPanelSessionsRow,
): CloseOtherPanelSessionsResult {
  return {
    currentSessionPublicId: row.current_session_public_id,
    closedSessionCount: row.closed_session_count,
  };
}

function mapCloseAllPanelSessionsRow(
  row: RpcCloseAllPanelSessionsRow,
): CloseAllPanelSessionsResult {
  return {
    currentSessionPublicId: row.current_session_public_id,
    closedSessionCount: row.closed_session_count,
  };
}

function firstRpcRow<T>(data: T[] | T | null): T | null {
  if (Array.isArray(data)) {
    return data[0] ?? null;
  }

  return data ?? null;
}

export async function openPanelSession(): Promise<PanelSession> {
  const { data, error } = await supabase.rpc('rpc_open_panel_session' as never);

  if (error) {
    throw normalizePanelSessionError(error);
  }

  const row = firstRpcRow(data as RpcPanelSessionRow[] | RpcPanelSessionRow | null);
  if (!row) {
    throw new Error('La apertura de panel session no devolvió datos.');
  }

  return mapPanelSessionRow(row);
}

export async function getMyPanelSessionState(): Promise<PanelSessionState> {
  const t0 =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();

  authDebug('getMyPanelSessionState.start');

  try {
    const { data, error } = await supabase.rpc('rpc_get_my_panel_session_state' as never);

    if (error) {
      authDebug('getMyPanelSessionState.rpc_error_before_throw', {
        durationMsApprox:
          typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? +(performance.now() - t0).toFixed(1)
            : null,
      });
      throw normalizePanelSessionError(error);
    }

    const row = firstRpcRow(data as RpcPanelSessionStateRow[] | RpcPanelSessionStateRow | null);
    if (!row) {
      authDebug('getMyPanelSessionState.empty_row_fallback', {
        durationMsApprox:
          typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? +(performance.now() - t0).toFixed(1)
            : null,
        hasValidPanelSession: false,
      });
      return {
        hasValidPanelSession: false,
        openedAt: null,
        expiresAt: null,
        failureCode: 'panel_session_required',
      };
    }

    const mapped = mapPanelSessionStateRow(row);
    authDebug('getMyPanelSessionState.ok', {
      durationMsApprox:
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? +(performance.now() - t0).toFixed(1)
          : null,
      hasValidPanelSession: mapped.hasValidPanelSession,
      failureCode: mapped.failureCode,
    });
    return mapped;
  } catch (err) {
    authDebug('getMyPanelSessionState.catch', {
      durationMsApprox:
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? +(performance.now() - t0).toFixed(1)
          : null,
      isPanelSessionError: err instanceof PanelSessionError,
      code: err instanceof PanelSessionError ? err.code : undefined,
    });
    throw err;
  }
}

export async function closeMyPanelSession(): Promise<boolean> {
  const { data, error } = await supabase.rpc('rpc_close_my_panel_session' as never);

  if (error) {
    throw normalizePanelSessionError(error);
  }

  return Boolean(data);
}

export async function recoverMyPanelSessionAccess(): Promise<PanelSessionRecoveryResult> {
  const { data, error } = await supabase.rpc('rpc_recover_my_panel_session_access' as never);

  if (error) {
    throw normalizePanelSessionError(error);
  }

  const row = firstRpcRow(data as RpcPanelSessionRecoveryRow[] | RpcPanelSessionRecoveryRow | null);
  if (!row) {
    throw new Error('La recuperación de panel session no devolvió datos.');
  }

  return mapPanelSessionRecoveryRow(row);
}

export async function listMyPanelSessions(): Promise<ManagedPanelSession[]> {
  const { data, error } = await supabase.rpc('rpc_list_my_panel_sessions' as never);

  if (error) {
    throw normalizePanelSessionError(error);
  }

  const rows = Array.isArray(data) ? data : data ? [data] : [];
  return (rows as RpcManagedPanelSessionRow[]).map(mapManagedPanelSessionRow);
}

export async function closeMyPanelSessionByPublicId(
  panelSessionPublicId: string,
): Promise<ClosePanelSessionByPublicIdResult> {
  const { data, error } = await supabase.rpc('rpc_close_my_panel_session_by_public_id' as never, {
    p_panel_session_public_id: panelSessionPublicId,
  } as never);

  if (error) {
    throw normalizePanelSessionError(error);
  }

  const row = firstRpcRow(
    data as RpcClosePanelSessionByPublicIdRow[] | RpcClosePanelSessionByPublicIdRow | null,
  );
  if (!row) {
    throw new Error('El cierre remoto de la panel session no devolvió datos.');
  }

  return mapClosePanelSessionByPublicIdRow(row);
}

export async function closeMyOtherPanelSessions(): Promise<CloseOtherPanelSessionsResult> {
  const { data, error } = await supabase.rpc('rpc_close_my_other_panel_sessions' as never);

  if (error) {
    throw normalizePanelSessionError(error);
  }

  const row = firstRpcRow(data as RpcCloseOtherPanelSessionsRow[] | RpcCloseOtherPanelSessionsRow | null);
  if (!row) {
    throw new Error('El cierre de las demás panel sessions no devolvió datos.');
  }

  return mapCloseOtherPanelSessionsRow(row);
}

export async function closeAllMyPanelSessions(): Promise<CloseAllPanelSessionsResult> {
  const { data, error } = await supabase.rpc('rpc_close_all_my_panel_sessions' as never);

  if (error) {
    throw normalizePanelSessionError(error);
  }

  const row = firstRpcRow(data as RpcCloseAllPanelSessionsRow[] | RpcCloseAllPanelSessionsRow | null);
  if (!row) {
    throw new Error('El cierre total de panel sessions no devolvió datos.');
  }

  return mapCloseAllPanelSessionsRow(row);
}